/**
 * About page component
 * Showcases Edicoo Agency's history, expertise, and competitive advantages
 * Features a professional background image with overlay for the hero section
 */

import React from 'react'
import { Link } from 'react-router'
import { Button } from '../components/ui/button'
import { CheckCircle, Users, Target, Globe, Award, TrendingUp } from 'lucide-react'

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Background Image */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/43ca0b48-5bcb-4b46-8a60-b64030c1a09e.png"
            alt="Edicoo Agency Office"
            className="w-full h-full object-cover"
          />
          {/* Dark Overlay for Readability */}
          <div className="absolute inset-0 bg-black/50" />
        </div>
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">About Us</h1>
          <div className="bg-orange-600/90 backdrop-blur-sm inline-block px-6 py-4 rounded-lg">
            <p className="text-2xl font-semibold text-white">
              Iraq's Leading Agency. Connecting brands with millions of Iraqis daily.
            </p>
          </div>
          <p className="text-xl text-orange-100 mt-6 max-w-3xl mx-auto">
            Founded in 2008, Edicoo is one of Iraq's most trusted advertising and media agencies, 
            providing end-to-end solutions in digital marketing, influencer collaborations, and media planning & buying.
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-6 text-sm text-gray-700 leading-relaxed">
                <p>
                  Founded in 2008, Edicoo is a leading media planning, media buying, and digital marketing agency in Iraq, serving brands across the Kurdistan Region and the wider Iraqi market. The agency develops data-driven advertising strategies based on comprehensive audience analysis, ensuring precise targeting and effective campaign performance.
                </p>
                <p>
                  Edicoo plans and executes advertising campaigns across Television, Outdoor, Radio, Print, and Digital platforms, including emerging media channels. With strong media negotiation expertise, the agency secures optimal placements and visibility across both Arabic and Kurdish media environments.
                </p>
                <p>
                  Alongside traditional media services, Edicoo delivers integrated digital marketing solutions, including social media marketing, influencer marketing, social media management, and paid advertising, driving measurable results and meaningful audience engagement.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/112fddec-2cf3-4409-9dd3-8f06e1850bb7.jpg"
                alt="Edicoo Team Collaboration"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">15+ Years</h3>
              <p className="text-gray-600">Of trusted experience in Iraqi market</p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Millions Managed</h3>
              <p className="text-gray-600">In annual advertising spend</p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">4 Languages</h3>
              <p className="text-gray-600">Arabic, Kurdish, English, Turkish</p>
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Advantages */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Goal</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We use various strategies to boost your brand awareness, attract potential leads, and encourage conversions. By choosing the right platforms, timing, and tailored messages, our media planners aim to make a brand more visible, capture interested leads, and turn them into loyal customers. These efforts are all geared towards helping brands achieve their specific goals through effective advertising and audience engagement.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Traditional + Digital</h3>
              <p className="text-gray-600">Seamlessly blending TV, radio, and print with modern digital strategies</p>
            </div>
            
            <div className="text-center p-6">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Local Expertise</h3>
              <p className="text-gray-600">Deep understanding of Iraqi culture and consumer behavior</p>
            </div>
            
            <div className="text-center p-6">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Multi-Language</h3>
              <p className="text-gray-600">Full campaigns in Arabic, Kurdish, English, and Turkish</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-900 to-orange-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">Ready to Dominate the Iraqi Market?</h2>
          <p className="text-xl text-orange-100 mb-8">
            Partner with Iraq's leading advertising agency to connect with millions of consumers
          </p>
          <Button asChild size="lg" className="bg-white text-orange-700 hover:bg-orange-50 text-lg px-8">
            <Link to="/contact">Start Your Campaign</Link>
          </Button>
        </div>
      </section>
    </div>
  )
}
